import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Inbox, Search } from 'lucide-react';
import { useActivityFeed, type ActivityFilterKind } from '@/lib/useActivityFeed';
import { useTodayStats } from '@/lib/useTodayStats';
import { ActivityDateSection } from '@/components/activity/ActivityDateSection';
import { ActivityFilters } from '@/components/activity/ActivityFilters';

function fmtHMS(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export default function Activity() {
  const [filter, setFilter] = useState<ActivityFilterKind>('all');
  const [search, setSearch] = useState('');
  const { groups, total, todaySummary, isLoading } = useActivityFeed(filter, search);
  const stats = useTodayStats();
  const qc = useQueryClient();

  return (
    <div className="p-5 max-w-[1100px] mx-auto">
      {/* Page header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h1 className="text-[18px] font-bold text-slate-900 tracking-tight">Aktivite</h1>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            Bugünkü çağrı geçmişi, statü değişimleri ve özet
          </p>
        </div>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ['activity-feed'] })}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface ring-1 ring-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition"
        >
          <RefreshCw size={13} />
          Yenile
        </button>
      </div>

      {/* Today summary — Gelen/Giden kırılımı + görüşme/aktarım/durum (detaylı) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-3">
        <SummaryCard
          label="Gelen" value={String(stats.inbound.total)} tone="emerald"
          sub={<><span className="text-emerald-600">✓ {stats.inbound.answered}</span><span className="text-slate-300 mx-1">·</span><span className={stats.inbound.missed > 0 ? 'text-red-500' : 'text-slate-400'}>✗ {stats.inbound.missed}</span></>}
        />
        <SummaryCard label="Giden" value={String(stats.outbound.total)} tone="amber" />
        <SummaryCard label="Toplam görüşme" value={fmtHMS(stats.totalTalkSec)} tone="brand" />
        <SummaryCard label="Aktarım" value={String(stats.transfers)} tone="brand" />
        <SummaryCard label="Durum değişimi" value={String(todaySummary.statusChanges + todaySummary.queueChanges)} tone="amber" />
      </div>

      {/* Filter row */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <ActivityFilters value={filter} onChange={setFilter} />
        <div className="flex items-center gap-3 ml-auto">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Numara / etiket ara"
              className="pl-7 pr-3 py-1.5 rounded-lg text-[12px] bg-surface ring-1 ring-slate-200 focus:ring-brand-300 outline-none w-[180px]"
            />
          </div>
          <span className="text-[11px] text-slate-400 whitespace-nowrap">{total} kayıt</span>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <SkeletonList />
      ) : groups.length === 0 ? (
        <EmptyState />
      ) : (
        <div>
          {groups.map((g) => (
            <ActivityDateSection key={g.bucket} group={g} defaultOpen={g.bucket === 'today'} />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone, sub }: {
  label: string; value: string; tone: 'brand' | 'emerald' | 'amber'; sub?: React.ReactNode;
}) {
  const toneClasses = {
    brand: 'text-brand-700',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
  }[tone];
  return (
    <div className="bg-surface rounded-2xl ring-1 ring-slate-200/70 p-3 shadow-sm">
      <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">{label}</div>
      <div className={`text-[24px] font-bold tabular-nums tracking-tight leading-none ${toneClasses}`}>{value}</div>
      {sub && <div className="text-[11px] font-semibold tabular-nums mt-1.5">{sub}</div>}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-surface rounded-2xl ring-1 ring-slate-200/70 py-14 px-4 text-center">
      <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <Inbox size={20} className="text-slate-400" />
      </div>
      <p className="text-[13.5px] font-medium text-slate-700">Henüz aktivite yok</p>
      <p className="text-[11.5px] text-slate-500 mt-1">İlk çağrını alınca burada görünecek</p>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="bg-surface rounded-xl ring-1 ring-slate-200/70 px-3 py-2.5 flex items-center gap-3 animate-pulse">
          <div className="w-[88px] h-7 bg-slate-100 rounded" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-24 bg-slate-100 rounded" />
            <div className="h-2.5 w-40 bg-slate-100 rounded" />
          </div>
          <div className="w-12 h-4 bg-slate-100 rounded" />
        </div>
      ))}
    </div>
  );
}
